import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, CreditCard, Users, FileText, Home, LogOut, Video, CheckCircle, DollarSign, Key, ChevronDown, CalendarPlus, Trash2, Mail, MessageSquare } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link, useNavigate } from "react-router-dom";
import AdvancedAvailabilityManager from "@/components/AdvancedAvailabilityManager";
import MentorProfileUpload from "@/components/MentorProfileUpload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminW9Manager } from "@/components/AdminW9Manager";
import { BackgroundCheckManager } from "@/components/BackgroundCheckManager";
import AdminSupportTickets from "@/components/AdminSupportTickets";
import { MentorProfileEditor } from "@/components/MentorProfileEditor";
import { NotificationBell } from "@/components/NotificationBell";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { MentorVisibilityToggle } from "@/components/MentorVisibilityToggle";
import { StudentManager } from "@/components/StudentManager";
import { AdminAnalyticsDashboard } from "@/components/analytics/AdminAnalyticsDashboard";
import ComplianceTools from "@/components/admin/ComplianceTools";
import AdminRefundManager from "@/components/AdminRefundManager";
import { SystemHealthMonitor } from "@/components/admin/SystemHealthMonitor";
import { AdminTestingPanel } from "@/components/AdminTestingPanel";
import { YearlyEarningsManager } from "@/components/admin/YearlyEarningsManager";
import { formatTime12Hour } from "@/lib/utils";
import { getStoredTimezone, getTimezoneAbbr } from "@/lib/timezone-utils";
import ComplianceFooter from "@/components/ComplianceFooter";
import { AdminSessionCalendar } from "@/components/admin/AdminSessionCalendar";
import { CompletedSessionsManager } from "@/components/admin/CompletedSessionsManager";

const SessionsManagement = ({ fetchAllSessions }: { fetchAllSessions: () => Promise<any[]> }) => {
  const [allSessions, setAllSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadAllSessions = async () => {
    setLoading(true);
    try {
      const sessions = await fetchAllSessions();
      setAllSessions(sessions);
    } catch (error) {
      console.error('Error loading all sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-medium">Platform Sessions</h4>
        <Button onClick={loadAllSessions} disabled={loading} size="sm">
          {loading ? "Loading..." : "Load All Sessions"}
        </Button>
      </div>
      
      {allSessions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {loading ? "Loading sessions..." : "Click 'Load All Sessions' to view all platform sessions"}
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {allSessions.map((session) => (
            <div key={session.id} className="border border-border rounded-lg p-3 text-sm">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">
                    {session.profiles?.first_name} {session.profiles?.last_name} → {session.mentors?.first_name} {session.mentors?.last_name}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} at {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} {getTimezoneAbbr(getStoredTimezone())}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Student: {session.profiles?.email} | Mentor: {session.mentors?.email}
                  </p>
                </div>
                <Badge variant={session.status === 'completed' ? 'default' : 'outline'}>
                  {session.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  // Set page title
  useEffect(() => {
    document.title = "Admin Dashboard - Ascendry";
  }, []);
  const [applications, setApplications] = useState<any[]>([]);
  const [approvedMentors, setApprovedMentors] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [usersWithoutApplications, setUsersWithoutApplications] = useState<any[]>([]);
  const [adminProfile, setAdminProfile] = useState<any>(null);
  const [adminMentor, setAdminMentor] = useState<any>(null);
  const [stats, setStats] = useState({
    totalBookings: 0,
    totalRevenue: 0,
    activeMentors: 0,
    pendingApplications: 0
  });
  const [feeAnalytics, setFeeAnalytics] = useState({
    studentTransactionFees: 0,
    mentorPlatformFees: 0,
    totalFeeRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  // Collapsible section states
  const [analyticsOpen, setAnalyticsOpen] = useState(true);
  const [userManagementOpen, setUserManagementOpen] = useState(false);
  const [systemOperationsOpen, setSystemOperationsOpen] = useState(false);
  const [mentorAvailabilityOpen, setMentorAvailabilityOpen] = useState(false);
  const [mentorProfileOpen, setMentorProfileOpen] = useState(false);
  
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out.",
      });
      
      navigate('/');
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/auth/mentor');
        return;
      }

      // Check if user is actually an admin
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', session.user.id)
        .single();
      
      if (!profile || profile.role !== 'admin') {
        toast({
          title: "Access Denied",
          description: "You need admin privileges to access this page.",
          variant: "destructive",
        });
        navigate('/auth/mentor');
        return;
      }

      fetchApplications();
      fetchApprovedMentors();
      fetchUsersWithoutApplications();
      fetchAdminData(session.user);
      fetchStats();
      fetchFeeAnalytics();
    };

    checkAuthAndFetch();

    // Listen for auth state changes to handle session expiration
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Admin Dashboard - Auth state change:', event);
        
        if (event === 'SIGNED_OUT' || !session) {
          // Session expired or user signed out
          navigate('/auth/mentor');
          return;
        }
        
        if (event === 'TOKEN_REFRESHED' && session) {
          // Token was refreshed, verify admin status again
          setTimeout(async () => {
            const { data: profile } = await supabase
              .from('profiles')
              .select('role')
              .eq('user_id', session.user.id)
              .single();
            
            if (!profile || profile.role !== 'admin') {
              navigate('/auth/mentor');
            }
          }, 0);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, toast]);

  const fetchApplications = async () => {
    try {
      const { data, error } = await supabase
        .from('mentor_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast({
        title: "Error",
        description: "Failed to fetch mentor applications.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedMentors = async () => {
    try {
      const { data, error } = await supabase
        .from('mentors')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setApprovedMentors(data || []);
    } catch (error) {
      console.error('Error fetching mentors:', error);
    }
  };

  const fetchSessions = async (mentorData?: any) => {
    try {
      // Use passed mentor data or fallback to state
      const mentor = mentorData || adminMentor;
      
      // If admin is also a mentor, only show their mentor sessions
      let query = supabase
        .from('sessions')
        .select(`
          *,
          mentors (first_name, last_name, specialty, email),
          profiles!sessions_student_id_fkey (first_name, last_name, email)
        `)
        .neq('status', 'cancelled'); // Filter out cancelled sessions
      
      // Filter by mentor_id if admin is also a mentor
      if (mentor?.id) {
        query = query.eq('mentor_id', mentor.id);
      } else {
        // If admin is not a mentor, show no sessions in the main dashboard
        setSessions([]);
        return;
      }
      
      const { data, error } = await query.order('scheduled_at', { ascending: false });
      
      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  // Separate function to fetch ALL sessions for admin management
  const fetchAllSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          mentors (first_name, last_name, specialty, email),
          profiles!sessions_student_id_fkey (first_name, last_name, email)
        `)
        .order('scheduled_at', { ascending: false }); // Newest first, show all statuses
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching all sessions:', error);
      return [];
    }
  };

  const fetchAdminData = async (user: any) => {
    try {
      // Get admin profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      setAdminProfile(profile);

      // Check if admin also has a mentor record (for conducting sessions)
      const { data: mentor } = await supabase
        .from('mentors')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (mentor) {
        setAdminMentor(mentor);
        // Pass mentor data directly to fetchSessions to avoid state timing issues
        await fetchSessions(mentor);
      } else {
        // No mentor record, so no sessions to show
        setSessions([]);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
  };

  const fetchUsersWithoutApplications = async () => {
    try {
      // Get all mentor profiles and their application status in one query
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          user_id, 
          email, 
          first_name, 
          last_name, 
          created_at, 
          role
        `)
        .eq('role', 'mentor')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get all application emails to exclude
      const { data: applications, error: appError } = await supabase
        .from('mentor_applications')
        .select('email');

      if (appError) throw appError;

      const applicationEmails = new Set(applications?.map(app => app.email.toLowerCase().trim()) || []);
      
      // Filter out users who have submitted applications
      const usersWithoutApps = (data || []).filter(user => {
        const userEmail = user.email?.toLowerCase().trim();
        return userEmail && !applicationEmails.has(userEmail);
      });

      console.log('All mentor profiles:', data?.length);
      console.log('Application emails:', applicationEmails);
      console.log('Users without applications:', usersWithoutApps.length);

      setUsersWithoutApplications(usersWithoutApps);
    } catch (error) {
      console.error('Error fetching users without applications:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // Get total bookings
      const { data: allSessions } = await supabase
        .from('sessions')
        .select('id, status, mentor_payout_amount');

      // Get payment transactions for revenue
      const { data: transactions } = await supabase
        .from('payment_transactions')
        .select('amount_paid')
        .eq('status', 'completed');

      // Get active mentors count
      const { data: activeMentorsData } = await supabase
        .from('mentors')
        .select('id')
        .eq('is_active', true);

      // Get pending applications count directly from database
      const { data: pendingApps } = await supabase
        .from('mentor_applications')
        .select('id')
        .eq('status', 'pending');

      setStats({
        totalBookings: allSessions?.length || 0,
        totalRevenue: (transactions?.reduce((sum, t) => sum + t.amount_paid, 0) || 0) / 100,
        activeMentors: activeMentorsData?.length || 0,
        pendingApplications: pendingApps?.length || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFeeAnalytics = async () => {
    try {
      // Get student transaction fees
      const { data: studentFees } = await supabase
        .from('payment_transactions')
        .select('transaction_fee')
        .eq('status', 'completed')
        .not('transaction_fee', 'is', null);

      // Get mentor platform fees from completed sessions
      const { data: mentorFees } = await supabase
        .from('sessions')
        .select('platform_fee_amount')
        .eq('status', 'completed')
        .not('platform_fee_amount', 'is', null);

      const studentFeeTotal = (studentFees?.reduce((sum, t) => sum + (t.transaction_fee || 0), 0) || 0) / 100;
      const mentorFeeTotal = (mentorFees?.reduce((sum, s) => sum + (s.platform_fee_amount || 0), 0) || 0); // Already in dollars

      setFeeAnalytics({
        studentTransactionFees: studentFeeTotal,
        mentorPlatformFees: mentorFeeTotal,
        totalFeeRevenue: studentFeeTotal + mentorFeeTotal
      });
    } catch (error) {
      console.error('Error fetching fee analytics:', error);
    }
  };

  const handleApplicationAction = async (applicationId: string, action: 'approved' | 'denied') => {
    try {
      if (action === 'approved') {
        console.log('🟡 Attempting to approve application:', applicationId);
        console.log('🟡 Current user session:', await supabase.auth.getSession());
        
        // Call the edge function to approve the mentor application using direct fetch
        const session = await supabase.auth.getSession();
        const response = await fetch(`https://llcojpvgertoimsualob.supabase.co/functions/v1/approve-mentor-application`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.data.session?.access_token}`,
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxsY29qcHZnZXJ0b2ltc3VhbG9iIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQyNzMwODAsImV4cCI6MjA2OTg0OTA4MH0.mPKMSynGdAZknGpnaZFqY9Xa1S8cxg2u1LlpJfiRc5I',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ applicationId })
        });

        const data = await response.json();
        console.log('🟡 Function response:', { data, status: response.status });

        if (!response.ok || data.error) {
          console.error('🔴 Function error details:', data);
          throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        toast({
          title: "Success",
          description: "Mentor application approved successfully! Approval email sent.",
        });
      } else {
        // Handle denial the old way
        const { error: updateError } = await supabase
          .from('mentor_applications')
          .update({ 
            status: action,
            reviewed_at: new Date().toISOString(),
            reviewed_by: adminProfile?.user_id
          })
          .eq('id', applicationId);

        if (updateError) throw updateError;

        toast({
          title: "Success",
          description: `Application ${action} successfully.`,
        });
      }

      fetchApplications(); // Refresh the list
      fetchApprovedMentors(); // Refresh mentors list
      fetchUsersWithoutApplications(); // Refresh users without apps
      fetchStats(); // Refresh stats
      fetchFeeAnalytics(); // Refresh fee analytics
    } catch (error: any) {
      console.error('Error updating application:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update application.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteApplication = async (applicationId: string, email: string) => {
    try {
      // First, try to find and delete the user account
      const { data: listResponse, error: userLookupError } = await supabase.auth.admin.listUsers();
      
      if (!userLookupError && listResponse?.users) {
        const existingUser = listResponse.users.find((u: any) => u.email === email);
        
        if (existingUser) {
          // Delete the user account
          const { error: deleteUserError } = await supabase.auth.admin.deleteUser(existingUser.id);
          if (deleteUserError) {
            console.error('Error deleting user account:', deleteUserError);
          }
        }
      }

      // Delete the application
      const { error: deleteAppError } = await supabase
        .from('mentor_applications')
        .delete()
        .eq('id', applicationId);

      if (deleteAppError) throw deleteAppError;

      toast({
        title: "Success",
        description: "Test mentor account and application deleted successfully.",
      });

      fetchApplications(); // Refresh the list
      fetchUsersWithoutApplications(); // Refresh users without apps
      fetchStats(); // Refresh stats
    } catch (error: any) {
      console.error('Error deleting application:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete application.",
        variant: "destructive",
      });
    }
  };

  const handleAddToCalendar = async (sessionId: string) => {
    console.log('🔍 Admin calendar download function called with sessionId:', sessionId);
    console.log('🔍 Function type:', typeof sessionId);
    
    if (!sessionId) {
      console.error('❌ No sessionId provided');
      toast({
        title: "Error",
        description: "No session ID provided for calendar download.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('📅 Starting admin calendar download for session:', sessionId);
      console.log('📅 Using direct fetch approach...');
      
      // Get current session for auth
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }
      
      // Make direct fetch to edge function
      const response = await fetch(`https://llcojpvgertoimsualob.supabase.co/functions/v1/generate-calendar-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxsY29qcHZnZXJ0b2ltc3VhbG9iIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQyNzMwODAsImV4cCI6MjA2OTg0OTA4MH0.mPKMSynGdAZknGpnaZFqY9Xa1S8cxg2u1LlpJfiRc5I'
        },
        body: JSON.stringify({ sessionId })
      });

      console.log('📅 Direct fetch response:', { 
        status: response.status, 
        ok: response.ok,
        statusText: response.statusText 
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Get the ICS content as text
      const icsContent = await response.text();
      console.log('📅 Received ICS content length:', icsContent.length);

      if (!icsContent || icsContent.length === 0) {
        throw new Error('Empty calendar content received');
      }

      console.log('📅 Creating admin blob and download...');

      // Create blob and download
      const blob = new Blob([icsContent], { type: 'text/calendar; charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `mentoring-session-${sessionId}.ics`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('📅 Admin download completed successfully');

      toast({
        title: "Calendar File Downloaded",
        description: "Session has been added to your calendar file.",
      });
    } catch (error: any) {
      console.error('📅 Admin calendar download error:', error);
      console.error('📅 Admin error stack:', error.stack);
      console.error('📅 Admin error name:', error.name);
      console.error('📅 Admin error message:', error.message);
      
      toast({
        title: "Error",
        description: error.message || "Failed to generate calendar file. Please try again.",
        variant: "destructive",
      });
    }
  };


  const handleDeleteSession = async (sessionId: string) => {
    setSessionToDelete(sessionId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;

    try {
      // First refund credits to the student
      const { error: refundError } = await supabase.rpc('refund_session_credits', {
        p_session_id: sessionToDelete,
        p_user_id: adminProfile?.user_id
      });

      if (refundError) {
        console.error('Error refunding credits:', refundError);
        // Continue with deletion even if refund fails
      }

      // Update session status to 'cancelled' instead of deleting
      const { error } = await supabase
        .from('sessions')
        .update({ 
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionToDelete);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Session cancelled and credits refunded to student.",
      });

      fetchSessions(); // Refresh the sessions list
      fetchStats(); // Refresh stats
    } catch (error: any) {
      console.error('Error deleting session:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to cancel session.",
        variant: "destructive",
      });
    } finally {
      setShowDeleteDialog(false);
      setSessionToDelete(null);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim() || !newPassword.trim()) {
      toast({
        title: "Error",
        description: "Please enter both email and new password.",
        variant: "destructive",
      });
      return;
    }

    setResetting(true);
    try {
      // Find the user by email
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, role')
        .eq('email', resetEmail.trim())
        .single();

      if (profileError || !profile) {
        toast({
          title: "Error", 
          description: "User not found with that email address.",
          variant: "destructive",
        });
        return;
      }

      // Use admin privileges to update password
      const { error } = await supabase.auth.admin.updateUserById(
        profile.user_id,
        { password: newPassword }
      );

      if (error) throw error;

      toast({
        title: "Success",
        description: `Password updated for ${profile.first_name} ${profile.last_name} (${profile.role}).`,
      });

      setResetEmail("");
      setNewPassword("");
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reset password.",
        variant: "destructive",
      });
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-dark">
      <header className="border-b border-border obsidian-glass">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 md:space-x-4">
              <img 
                src="/lovable-uploads/e601b2a4-d63b-447f-b8f5-3e03625e19d6.png" 
                alt="Ascendry" 
                className="h-8 md:h-10 w-auto"
              />
              <Badge variant="secondary" className="hidden sm:inline-flex">Admin</Badge>
            </div>
            <div className="flex items-center space-x-1 md:space-x-4">
              <Button variant="ghost" size="sm" asChild className="hidden md:flex">
                <Link to="/">
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="md:hidden">
                <Link to="/">
                  <Home className="w-4 h-4" />
                </Link>
              </Button>
              <NotificationBell />
              <span className="text-muted-foreground hidden md:inline">Admin User</span>
              <Button variant="outline" size="sm" onClick={handleSignOut} className="hidden md:flex">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
              <Button variant="outline" size="sm" onClick={handleSignOut} className="md:hidden">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Dashboard Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-glow">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">Manage your platform operations</p>
          </div>
        </div>

        {/* Session Calendar */}
        <AdminSessionCalendar />

        {/* Admin Testing Panel */}
        <AdminTestingPanel />

        {/* Session History Section */}
        <section>
          <h2 className="text-xl font-semibold text-energy mb-4">Session History</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Sessions */}
            <Card className="obsidian-card">
              <CardHeader>
                <CardTitle className="text-energy">Upcoming Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {sessions.filter(s => ['scheduled', 'in_progress'].includes(s.status)).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No upcoming sessions scheduled
                    </div>
                  ) : (
                    sessions
                      .filter(s => ['scheduled', 'in_progress'].includes(s.status))
                      .slice(0, 5)
                      .map((session) => (
                        <div key={session.id} className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium">
                                {session.profiles?.first_name} {session.profiles?.last_name} ↔ {session.mentors?.first_name} {session.mentors?.last_name}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} at {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} {getTimezoneAbbr(getStoredTimezone())}
                              </p>
                            </div>
                            <Badge variant={session.status === 'scheduled' ? 'outline' : 'secondary'}>
                              {session.status}
                            </Badge>
                          </div>
                          <div className="text-sm">
                            <p><strong>Specialty:</strong> {session.mentors?.specialty}</p>
                            <p><strong>Duration:</strong> {session.duration_minutes || 60} minutes</p>
                            <p><strong>Notes:</strong> {session.notes || 'No notes provided'}</p>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAddToCalendar(session.id)}
                              className="flex items-center gap-2"
                            >
                              <CalendarPlus className="w-4 h-4" />
                              Calendar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              asChild
                              className="flex items-center gap-2"
                            >
                              <a href={`mailto:${session.profiles?.email || ''}?subject=Session Question - ${new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))}`}>
                                <MessageSquare className="w-4 h-4" />
                                Message
                              </a>
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteSession(session.id)}
                              className="flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Completed Sessions */}
            <Card className="obsidian-card">
              <CardHeader>
                <CardTitle className="text-energy">Completed Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {sessions.filter(s => s.status === 'completed').length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No completed sessions yet
                    </div>
                  ) : (
                    sessions
                      .filter(s => s.status === 'completed')
                      .slice(0, 5)
                      .map((session) => (
                        <div key={session.id} className="border rounded-lg p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium">
                                {session.profiles?.first_name} {session.profiles?.last_name} ↔ {session.mentors?.first_name} {session.mentors?.last_name}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                Completed: {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: getStoredTimezone() }).format(new Date(session.completed_at || session.scheduled_at))} {getTimezoneAbbr(getStoredTimezone())}
                              </p>
                            </div>
                            <Badge variant="default" className="bg-mentor-primary/10 text-mentor-primary">
                              Completed
                            </Badge>
                          </div>
                          <div className="text-sm">
                            <p><strong>Specialty:</strong> {session.mentors?.specialty}</p>
                            <p><strong>Duration:</strong> {session.duration_minutes || 60} minutes</p>
                            {session.mentor_payout_amount && (
                              <p><strong>Payout:</strong> ${(session.mentor_payout_amount / 100).toFixed(2)}</p>
                            )}
                            {session.platform_fee_amount && (
                              <p><strong>Platform Fee:</strong> ${(session.platform_fee_amount / 100).toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Analytics Overview Section */}
        <section>
          <h2 className="text-xl font-semibold text-energy mb-4">Platform Analytics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="obsidian-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-energy">Total Bookings</CardTitle>
                <Calendar className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalBookings}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.totalBookings === 0 ? 'No bookings yet' : 'Total session bookings'}
                </p>
              </CardContent>
            </Card>
            <Card className="obsidian-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-energy">Revenue</CardTitle>
                <CreditCard className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${stats.totalRevenue.toFixed(2)}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.totalRevenue === 0 ? 'No revenue yet' : 'Total revenue from credits'}
                </p>
              </CardContent>
            </Card>
            <Card className="obsidian-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-energy">Active Mentors</CardTitle>
                <Users className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.activeMentors}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.activeMentors === 0 ? 'No mentors yet' : 'Active mentors'}
                </p>
              </CardContent>
            </Card>
            <Card className="obsidian-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-energy">Pending Applications</CardTitle>
                <FileText className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.pendingApplications}</div>
                <p className="text-xs text-muted-foreground">Require review</p>
              </CardContent>
            </Card>
          </div>
        </section>


        {/* Advanced Analytics Dashboard Section */}
        <Collapsible open={analyticsOpen} onOpenChange={setAnalyticsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between text-left p-4 h-auto obsidian-card hover:bg-muted/5">
              <h2 className="text-xl font-semibold text-energy">Advanced Analytics Dashboard</h2>
              <ChevronDown className={`h-5 w-5 text-primary transition-transform duration-200 ${analyticsOpen ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            <AdminAnalyticsDashboard />
          </CollapsibleContent>
        </Collapsible>

        {/* User Management Section */}
        <Collapsible open={userManagementOpen} onOpenChange={setUserManagementOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between text-left p-4 h-auto obsidian-card hover:bg-muted/5">
              <h2 className="text-xl font-semibold text-energy">User Management</h2>
              <ChevronDown className={`h-5 w-5 text-primary transition-transform duration-200 ${userManagementOpen ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            <div className="max-w-4xl space-y-4">
              {/* Mentor Applications - Collapsible */}
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full max-w-3xl justify-between text-left p-3 h-auto obsidian-card hover:bg-muted/5">
                    <h3 className="text-lg font-semibold text-energy">Mentor Applications</h3>
                    <ChevronDown className="h-4 w-4 text-primary transition-transform duration-200" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4">
                  <Card className="obsidian-card">
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        {loading ? (
                          <div className="text-center py-8 text-muted-foreground">
                            Loading applications...
                          </div>
                        ) : applications.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            No applications submitted yet
                          </div>
                        ) : (
                          applications.map((application) => (
                             <div key={application.id} className="p-4 border border-border rounded-lg space-y-3">
                               <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                 <div className="flex-1">
                                   <div className="flex items-center justify-between sm:block">
                                     <h4 className="font-medium">{application.first_name} {application.last_name}</h4>
                                     <Badge 
                                       variant={
                                         application.status === 'pending' ? 'secondary' : 
                                         application.status === 'approved' ? 'default' : 'destructive'
                                       }
                                       className="sm:hidden"
                                     >
                                       {application.status}
                                     </Badge>
                                   </div>
                                   <p className="text-sm text-muted-foreground">{application.email}</p>
                                   <p className="text-sm text-muted-foreground">{application.phone}</p>
                                   <p className="text-sm text-muted-foreground">{application.specialty}</p>
                                 </div>
                                 <div className="hidden sm:block flex-shrink-0">
                                   <Badge 
                                     variant={
                                       application.status === 'pending' ? 'secondary' : 
                                       application.status === 'approved' ? 'default' : 'destructive'
                                     }
                                   >
                                     {application.status}
                                   </Badge>
                                 </div>
                               </div>
                               <div className="text-sm">
                                 <p><strong>Experience:</strong> {application.years_experience} years</p>
                                 <p><strong>Rate:</strong> ${(application.hourly_rate / 100).toFixed(0)}/hour</p>
                                 <p><strong>Expertise:</strong> {application.expertise?.join(', ')}</p>
                               </div>
                               {application.status === 'pending' && (
                                 <div className="flex gap-2">
                                   <Button 
                                     size="sm" 
                                     onClick={() => handleApplicationAction(application.id, 'approved')}
                                     className="flex-1 sm:flex-none"
                                   >
                                     <CheckCircle className="w-4 h-4 mr-1" />
                                     Approve
                                   </Button>
                                   <Button 
                                     size="sm" 
                                     variant="destructive"
                                     onClick={() => handleApplicationAction(application.id, 'denied')}
                                     className="flex-1 sm:flex-none"
                                   >
                                     Deny
                                   </Button>
                                   <Button 
                                     size="sm" 
                                     variant="outline"
                                     onClick={() => handleDeleteApplication(application.id, application.email)}
                                     className="flex-1 sm:flex-none"
                                   >
                                     Delete
                                   </Button>
                                 </div>
                               )}
                            </div>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </CollapsibleContent>
              </Collapsible>

              {/* Student Management - Collapsible */}
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full max-w-3xl justify-between text-left p-3 h-auto obsidian-card hover:bg-muted/5">
                    <h3 className="text-lg font-semibold text-energy">Student Management</h3>
                    <ChevronDown className="h-4 w-4 text-primary transition-transform duration-200" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4">
                  <StudentManager />
                </CollapsibleContent>
              </Collapsible>

              {/* Users Without Applications - Collapsible */}
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full max-w-3xl justify-between text-left p-3 h-auto obsidian-card hover:bg-muted/5">
                    <h3 className="text-lg font-semibold text-energy">Users Without Applications</h3>
                    <ChevronDown className="h-4 w-4 text-primary transition-transform duration-200" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4">
                  <Card className="obsidian-card">
                    <CardHeader>
                      <p className="text-sm text-muted-foreground">
                        These users created accounts but haven't submitted mentor applications yet
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {usersWithoutApplications.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            All users have submitted applications
                          </div>
                        ) : (
                          usersWithoutApplications.slice(0, 10).map((user) => (
                            <div key={user.user_id} className="p-4 border border-border rounded-lg space-y-3">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <div className="flex-1">
                                  <h4 className="font-medium">{user.first_name} {user.last_name}</h4>
                                  <p className="text-sm text-muted-foreground">{user.email}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Signed up: {new Date(user.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      const subject = "Complete Your Mentor Application";
                                      const body = `Hi ${user.first_name},\n\nWe noticed you created an account but haven't submitted your mentor application yet. Please complete your application here:\n\n${window.location.origin}/mentor-application\n\nThanks!\nAscendry Team`;
                                      window.open(`mailto:${user.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                                    }}
                                    className="flex-1 sm:flex-none"
                                  >
                                    Send Email
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </CollapsibleContent>
              </Collapsible>
              
              {/* Password Reset Tool - Not Collapsible */}
              <Card className="obsidian-card">
                <CardHeader>
                  <CardTitle className="text-energy flex items-center gap-2">
                    <Key className="h-5 w-5" />
                    Password Reset Tool
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handlePasswordReset} className="space-y-4">
                    <div>
                      <Label htmlFor="reset-email">User Email</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder="user@example.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-password">New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={resetting}
                      className="w-full"
                    >
                      {resetting ? "Resetting..." : "Reset Password"}
                    </Button>
                  </form>
                  <p className="text-sm text-muted-foreground mt-2">
                    This will immediately update the user's password.
                  </p>
                </CardContent>
              </Card>
              
              {/* All Sessions Management */}
              <Card className="obsidian-card">
                <CardHeader>
                  <CardTitle className="text-energy flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    All Sessions Management
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    View and manage all sessions across the platform
                  </p>
                </CardHeader>
                <CardContent>
                  <SessionsManagement fetchAllSessions={fetchAllSessions} />
                </CardContent>
              </Card>

              {/* Completed Sessions & Invoices */}
              <CompletedSessionsManager />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* System Operations Section */}
        <Collapsible open={systemOperationsOpen} onOpenChange={setSystemOperationsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between text-left p-4 h-auto obsidian-card hover:bg-muted/5">
              <h2 className="text-xl font-semibold text-energy">System Operations</h2>
              <ChevronDown className={`h-5 w-5 text-primary transition-transform duration-200 ${systemOperationsOpen ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            <div className="space-y-6">
              {/* Session Management */}
              <div className="space-y-6">
                {/* Upcoming Sessions */}
                <Card className="obsidian-card">
                  <CardHeader>
                    <CardTitle className="text-energy">Upcoming Sessions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {sessions.filter(s => ['scheduled', 'in_progress'].includes(s.status)).length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          No upcoming sessions scheduled
                        </div>
                      ) : (
                        sessions
                          .filter(s => ['scheduled', 'in_progress'].includes(s.status))
                          .map((session) => (
                            <div key={session.id} className="border border-border rounded-lg p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="font-medium">
                                    {session.profiles?.first_name} {session.profiles?.last_name} ↔ {session.mentors?.first_name} {session.mentors?.last_name}
                                  </h4>
                                  <p className="text-sm text-muted-foreground">
                                    {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} at {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} {getTimezoneAbbr(getStoredTimezone())}
                                  </p>
                                </div>
                                <Badge 
                                  variant={
                                    session.status === 'in_progress' ? 'secondary' : 'outline'
                                  }
                                >
                                  {session.status}
                                </Badge>
                              </div>
                              <div className="text-sm space-y-1">
                                <p><strong>Specialty:</strong> {session.mentors?.specialty}</p>
                                <p><strong>Duration:</strong> {session.duration_minutes || 60} minutes</p>
                                <p><strong>Notes:</strong> {session.notes || 'No notes provided'}</p>
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                {session.status === 'scheduled' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleAddToCalendar(session.id)}
                                    className="flex items-center gap-2"
                                  >
                                    <CalendarPlus className="w-4 h-4" />
                                    Add to Calendar
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  asChild
                                  className="flex items-center gap-2"
                                >
                                  <a href={`mailto:${session.profiles?.email || ''}?subject=Session Question - ${new Date(session.scheduled_at).toLocaleDateString()}`}>
                                    <MessageSquare className="w-4 h-4" />
                                    Message Student
                                  </a>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteSession(session.id)}
                                  className="flex items-center gap-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </CardContent>
                </Card>

              </div>

              {/* W9 Management */}
              <AdminW9Manager />
              
              {/* Background Check Management */}
              <BackgroundCheckManager />
              
              {/* Support Tickets */}
              <AdminSupportTickets />
              
              {/* Refund Management */}
              <AdminRefundManager />
              
              {/* GDPR Compliance Tools */}
              <ComplianceTools />
              
              {/* System Health Monitor */}
              <SystemHealthMonitor />
              
              {/* Yearly Earnings & 1099 Management */}
              <YearlyEarningsManager />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Admin Mentor Tools */}
        {adminMentor && (
          <div className="space-y-6">
            {/* My Mentor Availability Section */}
            <Collapsible open={mentorAvailabilityOpen} onOpenChange={setMentorAvailabilityOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between text-left p-4 h-auto obsidian-card hover:bg-muted/5">
                  <h2 className="text-xl font-semibold text-energy">My Mentor Availability</h2>
                  <ChevronDown className={`h-5 w-5 text-primary transition-transform duration-200 ${mentorAvailabilityOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4">
                <Card className="obsidian-card">
                  <CardContent className="pt-6">
                    <AdvancedAvailabilityManager mentorId={adminMentor.id} isAdmin={true} />
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>

            {/* My Mentor Profile Section */}
            <Collapsible open={mentorProfileOpen} onOpenChange={setMentorProfileOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between text-left p-4 h-auto obsidian-card hover:bg-muted/5">
                  <h2 className="text-xl font-semibold text-energy">My Mentor Profile</h2>
                  <ChevronDown className={`h-5 w-5 text-primary transition-transform duration-200 ${mentorProfileOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4 space-y-6">
                {/* Visibility Toggle */}
                <MentorVisibilityToggle 
                  mentorId={adminMentor.id} 
                  isActive={adminMentor.is_active}
                  onVisibilityChange={(isActive) => setAdminMentor(prev => ({ ...prev, is_active: isActive }))}
                />
                
                <Card className="obsidian-card">
                  <CardContent className="pt-6">
                    <MentorProfileEditor mentorId={adminMentor.id} isAdmin={true} />
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </main>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Session</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this session? This action cannot be undone. 
              The student will be automatically refunded their credits.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Session</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteSession} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Cancel Session & Refund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Footer */}
      <ComplianceFooter />
    </div>
  );
};

export default AdminDashboard;
